import { CdkTrapFocus } from '@angular/cdk/a11y';
import { isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, ElementRef, PLATFORM_ID, inject, signal, viewChild } from '@angular/core';

import { UiIconComponent, UiIconName } from '../ui/icon/icon.component';
import {
  A11Y_PREFERENCE_VALUES,
  A11Y_TEXT_SCALES,
  A11yPreferenceKey,
  A11yPreferences,
} from './a11y-preferences';
import { A11yPreferencesService } from './a11y-preferences.applier';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** Etykiety opcji — jedyna rzecz w tym module, która jest tekstem UI, a nie kontraktem. */
const OPTION_LABELS: Record<string, string> = {
  'textSize.smaller': 'Mniejszy',
  'textSize.normal': 'Domyślny',
  'textSize.large': 'Większy',
  'textSize.larger': 'Duży',
  'textSize.largest': 'Największy',
  'contrast.normal': 'Normalny',
  'contrast.high': 'Wysoki',
  'contrast.maximum': 'Maksymalny',
  'cursorSize.normal': 'Systemowy',
  'cursorSize.large': 'Duży',
  'cursorSize.larger': 'Większy',
  'cursorSize.largest': 'Największy',
};

/**
 * Klucze dwustanowe — WYPROWADZONE ze słownika, nie przepisane. Ósma opcja `off`/`on` dodana
 * w modelu rozszerzy ten typ sama; ręcznie przepisana lista rozjechałaby się bezgłośnie.
 */
type A11yToggleKey = {
  [K in A11yPreferenceKey]: A11yPreferences[K] extends 'off' | 'on' ? K : never;
}[A11yPreferenceKey];

/**
 * GLIF PRZY KAŻDYM PRZEŁĄCZNIKU jest częścią METADANYCH pozycji, a nie ozdobą wpisaną
 * w szablon. Makieta rysuje ikonę w każdym wierszu, a bramka `verify:s22-p10` (krok 1)
 * WYKONUJE tę tablicę i wymaga glifu przy każdej pozycji — wpis bez `icon` czerwieni ją,
 * czego skan po szablonie nie potrafiłby odróżnić od wiersza, który akurat renderuje się
 * z pętli.
 *
 * NAZWY POCHODZĄ Z ZAMKNIĘTEGO SŁOWNIKA `UI_ICON_NAMES` (faza 1) i są przybliżeniem, nie
 * dosłownością makiety: eksport rysuje „pół-koło" dla skali szarości, „A͟Z" dla dysleksji,
 * „U" dla podkreśleń i spinacz dla animacji, a słownik takich glifów NIE MA i dokładać do
 * niego nie wolno (teren fazy 1). Wybrane zamienniki: filtr = filtr barwy, plik = tekst,
 * oko = widoczność linku, klepsydra = ruch w czasie. Braki zgłoszone w raporcie fazy.
 */
const TOGGLES: readonly { key: A11yToggleKey; label: string; hint: string; icon: UiIconName }[] = [
  {
    key: 'grayscale',
    label: 'Skala szarości',
    hint: 'Usuwa kolory z całej strony.',
    icon: 'filtr',
  },
  {
    key: 'dyslexia',
    label: 'Tryb dla dysleksji',
    hint: 'Zwiększa odstępy między znakami, wyrazami i wierszami.',
    icon: 'plik',
  },
  {
    key: 'underlineLinks',
    label: 'Podkreślenie linków',
    hint: 'Odróżnia linki od tekstu nie tylko kolorem.',
    icon: 'oko',
  },
  {
    key: 'stopAnimations',
    label: 'Zatrzymaj animacje',
    hint: 'Wyłącza przejścia i ruch w całym panelu.',
    icon: 'klepsydra',
  },
];

/**
 * WIDGET DOSTĘPNOŚCI — przycisk w rogu ekranu i panel z siedmioma opcjami.
 *
 * NARZĘDZIE DOSTĘPNOŚCI MA BYĆ DOSTĘPNE. Stąd: pułapka fokusu z CDK (`cdkTrapFocus`) zamiast
 * ręcznej na dodatnich `tabindex` — `eslint.config.js` włącza cały preset `templateAccessibility`,
 * ale `no-positive-tabindex` NIE jest jego częścią, więc pułapka na dodatnich indeksach
 * przeszłaby lint bez słowa; `Escape` zamykający panel i ZWRACAJĄCY fokus na przycisk;
 * `aria-expanded` zmieniające się w obie strony; natywne `input[type=radio|checkbox]`
 * w `fieldset`/`legend` zamiast przełączników z `role="switch"` sklejonych z `<div>`.
 *
 * WYGLĄD ZMIENIŁ SIĘ, SEMANTYKA NIE (S-22 faza 5). Grupa segmentowa to nadal grupa radiowa:
 * natywny `input[type=radio]` LEŻY NA segmencie (przezroczysty, na całej jego powierzchni),
 * a rysunek robi `<label>`. Dzięki temu strzałki, `Home`/`End` i odczyt „2 z 5" pochodzą
 * z przeglądarki, a nie z ręcznego `role="radiogroup"`, którego trzeba by pilnować w każdym
 * stanie preferencji. Tak samo przełączniki: to `input[type=checkbox]` z `appearance: none`,
 * a nie `<div role="switch">`.
 *
 * PANEL ZOSTAJE W DOM PO ZAMKNIĘCIU (`[hidden]`), a nie znika przez `@if`. Powód jest wprost
 * dostępnościowy: `aria-controls` na przycisku wskazuje identyfikator, który musi ISTNIEĆ —
 * referencja w próżnię jest naruszeniem `aria-valid-attr-value`. `display: none` i tak zdejmuje
 * treść z drzewa dostępności, więc zamknięty panel nie jest dla nikogo hałasem.
 *
 * Z-INDEX PONIŻEJ SKIP-LINKU (`--empiria-z-skip-link: 1001`). Skip-link jest pierwszą rzeczą
 * w tabulacji na każdym ekranie; przykrycie go przez narzędzie dostępności byłoby zepsuciem
 * dostępności w imię dostępności.
 *
 * ZERO LOKALNYCH ZMIENNYCH CSS w bloku `styles:` — `verify-s19-p5.mjs:501-506` uznaje token
 * nierozwiązywalny w skompilowanym CSS za `unresolved` i pada. Widget czyta wyłącznie tokeny
 * zadeklarowane przez fazę 1.
 *
 * BIEL KARTY Z MAKIETY JEST DOKŁADNA OD 2026-08-17 (decyzja Filipa, Q-221). Do tego dnia była
 * PRZYBLIŻONA: karta stała na `--mat-sys-surface`, bo czysta biel mieszka w roli
 * `--mat-sys-surface-container-lowest`, a ta nie miała wpisu w rejestrze par
 * (`scripts/design-system/token-pairs.mjs`) — `verify:s19-p5` wymaga pokrycia dla każdej roli
 * koloru użytej w `src/**`, więc faza wolała cofnąć wygląd, niż ruszyć cudzy rejestr. To samo
 * obejście zrobiła niezależnie faza 3 w karcie ekranu szczegółów, i to właśnie DWA niezależne
 * ciche cofnięcia wygenerowały decyzję. Rola ma dziś zmierzone pary, więc karta stoi na
 * bieli, a tonalna stopka dalej na `--mat-sys-surface-container` — relacja „karta jaśniejsza
 * od stopki" z makiety zostaje, i to o krok wyraźniejszy niż przedtem.
 */
@Component({
  selector: 'app-a11y-widget',
  imports: [CdkTrapFocus, UiIconComponent],
  template: `
    <button
      #trigger
      type="button"
      class="a11y-trigger"
      aria-label="Ułatwienia dostępu"
      aria-controls="empiria-a11y-panel"
      [attr.aria-expanded]="isOpen()"
      (click)="toggle()"
    >
      <app-ui-icon name="dostepnosc" />
    </button>

    <div
      id="empiria-a11y-panel"
      class="a11y-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="empiria-a11y-panel-title"
      [class.a11y-panel--still]="reducedMotion()"
      [hidden]="!isOpen()"
      [cdkTrapFocus]="isOpen()"
      [cdkTrapFocusAutoCapture]="isOpen()"
      (keydown.escape)="close()"
    >
      <div class="a11y-panel__head">
        <h2 id="empiria-a11y-panel-title">
          <app-ui-icon name="dostepnosc" />
          Ułatwienia dostępu
        </h2>
        <!-- ✕ zamiast słowa „Zamknij": nazwa dostępna siedzi w aria-label, bo glif jest
             dekoracją i sam nie mówi nic. -->
        <button
          type="button"
          class="a11y-close"
          aria-label="Zamknij ułatwienia dostępu"
          (click)="close()"
        >
          <app-ui-icon name="zamknij" />
        </button>
      </div>

      <div class="a11y-panel__body">
        @for (group of groupsAboveToggles; track group.key) {
          <fieldset class="a11y-group">
            <legend>{{ group.legend }}</legend>
            <div class="a11y-segments" [class.a11y-segments--cards]="group.sample === 'swatch'">
              @for (option of group.options; track option; let index = $index) {
                <span
                  class="a11y-segment"
                  [class.a11y-segment--card]="group.sample === 'swatch'"
                  [class.a11y-segment--inverted]="option === 'maximum'"
                >
                  <input
                    type="radio"
                    class="a11y-segment__input"
                    [id]="group.key + '-' + option"
                    [name]="'empiria-a11y-' + group.key"
                    [checked]="preferences()[group.key] === option"
                    (change)="select(group.key, option)"
                  />
                  <label [for]="group.key + '-' + option">
                    @switch (group.sample) {
                      @case ('letter') {
                        <span class="a11y-letter" [class]="'a11y-step-' + (index + 1)" aria-hidden="true">A</span>
                        <span class="visually-hidden">{{ labelFor(group.key, option) }}</span>
                      }
                      @default {
                        <span class="a11y-swatch" aria-hidden="true"></span>
                        <span class="a11y-segment__text">{{ labelFor(group.key, option) }}</span>
                      }
                    }
                  </label>
                </span>
              }
            </div>
          </fieldset>
        }

        <!-- Legenda schowana wzrokowo, bo makieta pokazuje cztery wiersze bez nagłówka nad
             nimi — a fieldset bez legendy nie ma nazwy dostępnej. -->
        <fieldset class="a11y-group a11y-group--toggles">
          <legend class="visually-hidden">Przełączniki</legend>
          @for (toggle of toggles; track toggle.key) {
            <span class="a11y-toggle">
              <app-ui-icon class="a11y-toggle__icon" [name]="toggle.icon" />
              <span class="a11y-toggle__text">
                <label [for]="'toggle-' + toggle.key">{{ toggle.label }}</label>
                <span class="a11y-hint" [id]="'hint-' + toggle.key">{{ toggle.hint }}</span>
              </span>
              <input
                type="checkbox"
                class="a11y-switch"
                [id]="'toggle-' + toggle.key"
                [checked]="preferences()[toggle.key] === 'on'"
                [attr.aria-describedby]="'hint-' + toggle.key"
                (change)="toggleOption(toggle.key)"
              />
            </span>
          }
        </fieldset>

        @for (group of groupsBelowToggles; track group.key) {
          <fieldset class="a11y-group">
            <legend>{{ group.legend }}</legend>
            <div class="a11y-segments">
              @for (option of group.options; track option; let index = $index) {
                <span class="a11y-segment">
                  <input
                    type="radio"
                    class="a11y-segment__input"
                    [id]="group.key + '-' + option"
                    [name]="'empiria-a11y-' + group.key"
                    [checked]="preferences()[group.key] === option"
                    (change)="select(group.key, option)"
                  />
                  <label [for]="group.key + '-' + option">
                    <app-ui-icon
                      class="a11y-pointer"
                      [class]="'a11y-step-' + (index + 1)"
                      name="strzalka-wstecz"
                    />
                    <span class="visually-hidden">{{ labelFor(group.key, option) }}</span>
                  </label>
                </span>
              }
            </div>
          </fieldset>
        }
      </div>

      <div class="a11y-panel__foot">
        <button type="button" class="a11y-reset-all" (click)="resetAll()">
          <app-ui-icon name="odswiez" />
          Przywróć ustawienia domyślne
        </button>
      </div>
    </div>
  `,
  styles: `
    @use 'a11y';

    :host {
      display: contents;
    }

    @include a11y.visually-hidden;

    .a11y-trigger {
      position: fixed;
      right: var(--empiria-space-md);
      bottom: var(--empiria-space-md);
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 3.5rem;
      height: 3.5rem;
      padding: 0;
      border: 1px solid var(--mat-sys-outline);
      border-radius: var(--empiria-radius-circle);
      background: var(--mat-sys-primary);
      box-shadow: var(--empiria-elevation-2);
      color: var(--mat-sys-on-primary);
      cursor: pointer;
      font-size: 1.5rem;
    }

    .a11y-panel {
      position: fixed;
      right: var(--empiria-space-md);
      bottom: calc(3.5rem + var(--empiria-space-lg));
      z-index: 1000;
      display: flex;
      flex-direction: column;
      width: min(23rem, calc(100vw - var(--empiria-space-lg)));
      max-height: min(38rem, calc(100dvh - var(--empiria-space-2xl)));
      overflow: hidden auto;
      border: 1px solid var(--mat-sys-outline-variant);
      border-radius: var(--empiria-radius-lg);
      /* Czysta biel z makiety — patrz akapit „BIEL KARTY" w opisie komponentu. Stopka
         (.a11y-panel__foot) zostaje tonalna, żeby relacja odcieni się nie odwróciła.
         Komentarz nie waży NIC w budżecie stylu: build produkcyjny minifikuje arkusze
         komponentów i wycina komentarze — ostrzeżenie o budżecie tego widgetu urosło
         z 1,98 do 2,00 kB przez 18 znaków dłuższą NAZWĘ ROLI w deklaracji niżej. */
      background: var(--mat-sys-surface-container-lowest);
      box-shadow: var(--empiria-elevation-3);
      color: var(--mat-sys-on-surface);
      transition: opacity var(--empiria-motion-duration) ease-out;
    }

    .a11y-panel[hidden] {
      display: none;
    }

    .a11y-panel--still,
    .a11y-panel--still .a11y-switch,
    .a11y-panel--still .a11y-switch::after {
      transition: none;
    }

    .a11y-panel__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--empiria-space-xs);
      padding: var(--empiria-space-sm);
      border-bottom: 1px solid var(--mat-sys-outline-variant);
    }

    .a11y-panel__head h2 {
      display: flex;
      align-items: center;
      gap: var(--empiria-space-xs);
      margin: 0;
      font: var(--mat-sys-title-medium);
    }

    .a11y-close {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.25rem;
      height: 2.25rem;
      padding: 0;
      border: 1px solid transparent;
      border-radius: var(--empiria-radius-circle);
      background: transparent;
      color: var(--mat-sys-on-surface-variant);
      cursor: pointer;
      font-size: 1.25rem;
    }

    .a11y-close:hover {
      background: var(--mat-sys-surface-container);
    }

    .a11y-panel__body {
      display: flex;
      flex-direction: column;
      gap: var(--empiria-space-sm);
      padding: var(--empiria-space-sm);
    }

    .a11y-group {
      margin: 0;
      padding: 0;
      border: 0;
    }

    .a11y-group legend {
      padding: 0;
      margin-bottom: var(--empiria-space-xs);
      color: var(--mat-sys-on-surface);
      font: var(--mat-sys-title-small);
    }

    /* Grupa segmentowa: tonalna wanna, w niej równe segmenty. Pięć stopni „Rozmiaru tekstu"
       zamiast czterech z makiety — słownik preferencji ma pięć wartości i jest nietykalny. */
    .a11y-segments {
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
      gap: var(--empiria-space-2xs);
      padding: var(--empiria-space-2xs);
      border-radius: var(--empiria-radius-pill);
      background: var(--mat-sys-surface-container);
    }

    /* Kontrast to KARTY z próbką, nie pigułka — próbka potrzebuje kwadratu, nie paska. */
    .a11y-segments--cards {
      gap: var(--empiria-space-xs);
      padding: 0;
      border-radius: 0;
      background: transparent;
    }

    .a11y-segment {
      position: relative;
      display: flex;
    }

    /* Input LEŻY NA segmencie: niewidoczny, ale to on jest kontrolką — stąd strzałki,
       Home/End i odczyt „2 z 5" bez ani jednej linii własnej obsługi klawiatury. */
    .a11y-segment__input {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      margin: 0;
      opacity: 0;
      cursor: pointer;
    }

    .a11y-segment label {
      display: flex;
      flex: 1;
      align-items: center;
      justify-content: center;
      gap: var(--empiria-space-2xs);
      min-height: 2.25rem;
      padding: var(--empiria-space-2xs);
      border-radius: var(--empiria-radius-pill);
      color: var(--mat-sys-on-surface-variant);
      cursor: pointer;
      font: var(--mat-sys-label-large);
    }

    .a11y-segment__input:checked + label {
      background: var(--mat-sys-primary);
      color: var(--mat-sys-on-primary);
    }

    .a11y-segment__input:focus-visible + label {
      outline: 2px solid var(--mat-sys-primary);
      outline-offset: 2px;
    }

    .a11y-segment--card label {
      flex-direction: column;
      gap: var(--empiria-space-2xs);
      min-height: 4rem;
      border: 1px solid var(--mat-sys-outline-variant);
      border-radius: var(--empiria-radius-md);
      background: var(--mat-sys-surface-container);
      font: var(--mat-sys-label-medium);
    }

    /* „Maksymalny" pokazuje SOBĄ, co robi — karta jest ciemna, tak jak w makiecie. Kolor
       nie niesie tu znaczenia sam: pod próbką stoi słowo (WCAG 1.4.1). */
    .a11y-segment--inverted label {
      background: var(--mat-sys-on-surface);
      color: var(--mat-sys-surface);
    }

    .a11y-segment--card .a11y-segment__input:checked + label {
      border-color: var(--mat-sys-primary);
      border-width: 2px;
      background: var(--mat-sys-surface-container);
      color: var(--mat-sys-on-surface);
    }

    .a11y-segment--inverted .a11y-segment__input:checked + label {
      background: var(--mat-sys-on-surface);
      color: var(--mat-sys-surface);
    }

    .a11y-swatch {
      width: 1.5rem;
      height: 1.5rem;
      border: 1px solid var(--mat-sys-outline);
      border-radius: var(--empiria-radius-circle);
      background: linear-gradient(
        90deg,
        var(--mat-sys-surface) 0 50%,
        var(--mat-sys-on-surface) 50% 100%
      );
    }

    .a11y-letter {
      line-height: 1;
    }

    .a11y-step-1 {
      font-size: 0.75rem;
    }

    .a11y-step-2 {
      font-size: 0.95rem;
    }

    .a11y-step-3 {
      font-size: 1.15rem;
    }

    .a11y-step-4 {
      font-size: 1.35rem;
    }

    .a11y-step-5 {
      font-size: 1.6rem;
    }

    /* Strzałka kursora zbudowana z glifu „strzałka wstecz" obróconego o 45° — słownik
       fazy 1 nie ma glifu kursora, a dokładać do niego nie wolno. */
    .a11y-pointer {
      transform: rotate(45deg);
    }

    .a11y-group--toggles {
      display: flex;
      flex-direction: column;
      gap: var(--empiria-space-xs);
    }

    .a11y-toggle {
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: center;
      gap: var(--empiria-space-xs);
    }

    .a11y-toggle__icon {
      color: var(--mat-sys-on-surface-variant);
      font-size: 1.25rem;
    }

    .a11y-toggle__text {
      display: flex;
      flex-direction: column;
    }

    .a11y-toggle__text label {
      cursor: pointer;
      font: var(--mat-sys-body-medium);
    }

    .a11y-hint {
      color: var(--mat-sys-on-surface-variant);
      font: var(--mat-sys-body-small);
    }

    /* Przełącznik: nadal natywny checkbox, tylko przemalowany. Torem jest sam input,
       gałką jego ::after — żadnego <div role="switch"> do ręcznego oklawiaturowania. */
    .a11y-switch {
      position: relative;
      flex: none;
      width: 2.75rem;
      height: 1.5rem;
      margin: 0;
      appearance: none;
      border: 1px solid var(--mat-sys-outline);
      border-radius: var(--empiria-radius-pill);
      background: var(--mat-sys-surface-container);
      cursor: pointer;
      transition: background-color var(--empiria-motion-duration) ease-out;
    }

    .a11y-switch::after {
      content: '';
      position: absolute;
      top: 0.125rem;
      left: 0.125rem;
      width: 1.125rem;
      height: 1.125rem;
      border-radius: var(--empiria-radius-circle);
      background: var(--mat-sys-outline);
      transition: transform var(--empiria-motion-duration) ease-out;
    }

    .a11y-switch:checked {
      border-color: var(--mat-sys-primary);
      background: var(--mat-sys-primary);
    }

    .a11y-switch:checked::after {
      background: var(--mat-sys-on-primary);
      transform: translateX(1.25rem);
    }

    /* Tonalna stopka z JEDNYM przyciskiem — cztery „Przywróć" per grupa zniknęły razem
       z ramkami grup (makieta ma jeden reset, na dole, z ikoną odświeżenia). */
    .a11y-panel__foot {
      padding: var(--empiria-space-xs) var(--empiria-space-sm);
      border-top: 1px solid var(--mat-sys-outline-variant);
      background: var(--mat-sys-surface-container);
    }

    .a11y-reset-all {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: var(--empiria-space-xs);
      width: 100%;
      padding: var(--empiria-space-xs);
      border: 0;
      border-radius: var(--empiria-radius-pill);
      background: transparent;
      color: var(--mat-sys-primary);
      cursor: pointer;
      font: var(--mat-sys-label-large);
    }

    .a11y-reset-all:hover {
      background: var(--mat-sys-surface);
    }
  `,
})
export class A11yWidgetComponent {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly service = inject(A11yPreferencesService);

  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');

  protected readonly isOpen = signal(false);
  protected readonly reducedMotion = signal(false);
  protected readonly preferences = this.service.preferences;
  protected readonly toggles = TOGGLES;

  /**
   * Trzy opcje stopniowane. `textSize` idzie po MNOŻNIKU, a nie po kolejności ze słownika:
   * tam pierwsza pozycja jest wartością domyślną (kontrakt), a na ekranie suwak ma rosnąć.
   */
  private readonly gradedGroups = [
    {
      key: 'textSize' as const,
      legend: 'Rozmiar tekstu',
      sample: 'letter' as const,
      options: [...A11Y_PREFERENCE_VALUES.textSize].sort(
        (left, right) => A11Y_TEXT_SCALES[left] - A11Y_TEXT_SCALES[right],
      ),
    },
    {
      key: 'contrast' as const,
      legend: 'Kontrast',
      sample: 'swatch' as const,
      options: [...A11Y_PREFERENCE_VALUES.contrast],
    },
    {
      key: 'cursorSize' as const,
      legend: 'Rozmiar kursora',
      sample: 'pointer' as const,
      options: [...A11Y_PREFERENCE_VALUES.cursorSize],
    },
  ];

  /**
   * Kolejność z makiety: rozmiar tekstu i kontrast NAD przełącznikami, rozmiar kursora POD
   * nimi. Podział jest WYPROWADZONY z jednej listy — dwie ręcznie przepisane tablice
   * rozjechałyby się przy pierwszej zmianie słownika.
   */
  protected readonly groupsAboveToggles = this.gradedGroups.filter(
    (group) => group.key !== 'cursorSize',
  );

  protected readonly groupsBelowToggles = this.gradedGroups.filter(
    (group) => group.key === 'cursorSize',
  );

  constructor() {
    // Wzorzec z `shared/ui/loading-indicator`: podwójna bramka (platforma ORAZ istnienie
    // `matchMedia`), bo na serwerze nie ma `window`, a w jsdom `matchMedia` bywa nieobecne.
    if (!isPlatformBrowser(this.platformId) || typeof window.matchMedia !== 'function') {
      return;
    }

    const query = window.matchMedia(REDUCED_MOTION_QUERY);
    this.reducedMotion.set(query.matches);

    if (typeof query.addEventListener !== 'function') {
      return;
    }

    const onChange = (event: MediaQueryListEvent): void => this.reducedMotion.set(event.matches);
    query.addEventListener('change', onChange);
    this.destroyRef.onDestroy(() => query.removeEventListener('change', onChange));
  }

  protected labelFor(key: A11yPreferenceKey, option: string): string {
    return OPTION_LABELS[`${key}.${option}`] ?? option;
  }

  protected toggle(): void {
    this.isOpen.update((open) => !open);
  }

  /** `Escape` i ✕ oddają fokus przyciskowi — inaczej fokus wraca na `<body>` i ginie. */
  protected close(): void {
    if (!this.isOpen()) {
      return;
    }

    this.isOpen.set(false);
    this.trigger().nativeElement.focus();
  }

  protected select<K extends 'textSize' | 'contrast' | 'cursorSize'>(
    key: K,
    option: A11yPreferences[K],
  ): void {
    this.service.set(key, option);
  }

  protected toggleOption(key: A11yToggleKey): void {
    this.service.set(key, this.preferences()[key] === 'on' ? 'off' : 'on');
  }

  protected resetAll(): void {
    this.service.resetAll();
  }
}
